import gql from 'graphql-tag'
import { ApolloError } from '@apollo/client'
import { DocletContent, DocletHostMedia } from '../../doclets/types'

// GraphQL for MI6's doclets. "ShareDoclet" on the API side: doclets are
// standalone sharing sites, distinct from the legacy library Doclet type.

export const shareDocletFields = gql`
  fragment ShareDocletFields on ShareDoclet {
    id
    name
    displayName
    type
    draftContent
    draftVersion
    draftUpdatedAt
    draftUpdatedBy {
      id
      firstName
      lastName
    }
    isPublished
    hasUnpublishedChanges
    publishedAt
    shareUrl
    gating {
      modality
      password
      invitees
    }
    hostMedia {
      id
      url
      transcode
    }
    communities {
      id
      name
    }
    createdAt
    updatedAt
  }
`

export const getShareDocletQuery = gql`
  query shareDoclet($id: String!) {
    shareDoclet(id: $id) {
      ...ShareDocletFields
    }
  }
  ${shareDocletFields}
`

// Variable names match what the shared Table component sends
export const searchShareDocletsQuery = gql`
  query shareDoclets(
    $size: Int
    $offset: Int
    $sortBy: ShareDocletSortBy
    $sortDir: SearchEntitySortDir
    $query: String
  ) {
    shareDoclets(
      size: $size
      offset: $offset
      sortBy: $sortBy
      sortDir: $sortDir
      query: $query
    ) {
      total
      hits {
        id
        name
        displayName
        createdAt
        isPublished
        # Only the modality: the list never needs the password or invitees
        gating {
          modality
        }
        communities {
          id
          name
        }
      }
    }
  }
`

export const createShareDocletMutation = gql`
  mutation createShareDoclet {
    createShareDoclet {
      id
    }
  }
`

export const saveShareDocletDraftMutation = gql`
  mutation saveShareDocletDraft(
    $id: String!
    $content: JSON!
    $baseDraftVersion: Int!
  ) {
    saveShareDocletDraft(
      id: $id
      content: $content
      baseDraftVersion: $baseDraftVersion
    ) {
      id
      draftVersion
      draftUpdatedAt
      hasUnpublishedChanges
    }
  }
`

export const updateShareDocletMetadataMutation = gql`
  mutation updateShareDocletMetadata(
    $id: String!
    $name: String
    $type: DocletType
    $communityIds: [String!]
  ) {
    updateShareDocletMetadata(
      id: $id
      name: $name
      type: $type
      communityIds: $communityIds
    ) {
      id
      name
      displayName
      type
      communities {
        id
        name
      }
      updatedAt
    }
  }
`

// Other editors' saves as they happen; see useDocletAutosave for how they're
// told apart from this editor's own
export const shareDocletUpdatedSubscription = gql`
  subscription shareDocletUpdated($docletId: String!) {
    shareDocletUpdated(docletId: $docletId) {
      id
      draftVersion
      draftUpdatedBy {
        id
        firstName
        lastName
      }
    }
  }
`

// Cheap check for saves missed while the websocket was down or the tab idle
export const shareDocletDraftVersionQuery = gql`
  query shareDocletDraftVersion($id: String!) {
    shareDoclet(id: $id) {
      id
      draftVersion
      draftUpdatedBy {
        id
        firstName
        lastName
      }
    }
  }
`

export interface EditorPerson {
  id: string
  firstName: string
  lastName: string
}

export interface ShareDocletGatingData {
  modality: 'direct' | 'collectEmail' | 'password' | 'invited'
  /** Stored readable, so editors can send it on */
  password: string | null
  /** Lower-cased email addresses and "@domain" entries */
  invitees: string[]
}

export interface ShareDocletData {
  id: string
  /** Set in document settings; null means named by the page title */
  name: string | null
  /** name, else the first Title block's title (as of the last save) */
  displayName: string | null
  type: string | null
  draftContent: DocletContent
  draftVersion: number
  draftUpdatedAt: string | null
  draftUpdatedBy: EditorPerson | null
  isPublished: boolean
  hasUnpublishedChanges: boolean
  /** The last publish; kept after unpublishing */
  publishedAt: string | null
  /** The share link; null until the first publish */
  shareUrl: string | null
  /** Who can open the share link */
  gating: ShareDocletGatingData
  /** The draft's Media blocks' host media records, current as of loading */
  hostMedia: DocletHostMedia[]
  communities: { id: string; name: string }[]
  createdAt: string
  updatedAt: string
}

// Soft delete: the doclet leaves MI6 and its share link goes offline
export const deleteShareDocletMutation = gql`
  mutation deleteShareDoclet($id: String!) {
    deleteShareDoclet(id: $id)
  }
`

// The API's NOT_FOUND: the doclet doesn't exist, or has been deleted
export const isNotFoundError = (error: unknown): boolean =>
  ((error as ApolloError)?.graphQLErrors ?? []).some(
    e => e.extensions?.code === 'NOT_FOUND'
  )

/**
 * When a save or publish was based on a draft someone else has since saved
 * over, the API rejects it with DOCLET_DRAFT_CONFLICT and the current version.
 * Returns that version, or null for any other error.
 */
export const getDraftConflictVersion = (error: unknown): number | null => {
  const graphQLErrors = (error as ApolloError)?.graphQLErrors ?? []
  const conflict = graphQLErrors.find(
    e => e.extensions?.code === 'DOCLET_DRAFT_CONFLICT'
  )
  const version = conflict?.extensions?.currentDraftVersion
  return typeof version === 'number' ? version : null
}

// Publishing returns the doclet's fields so the cache (and the builder's
// status and share link) update in place
export const publishShareDocletMutation = gql`
  mutation publishShareDoclet($id: String!, $baseDraftVersion: Int!) {
    publishShareDoclet(id: $id, baseDraftVersion: $baseDraftVersion) {
      ...ShareDocletFields
    }
  }
  ${shareDocletFields}
`

// Gating applies straight away, published or not, and returns the doclet so
// the cache updates in place
export const updateShareDocletGatingMutation = gql`
  mutation updateShareDocletGating(
    $id: String!
    $gating: ShareDocletGatingInput!
  ) {
    updateShareDocletGating(id: $id, gating: $gating) {
      ...ShareDocletFields
    }
  }
  ${shareDocletFields}
`

export const unpublishShareDocletMutation = gql`
  mutation unpublishShareDoclet($id: String!) {
    unpublishShareDoclet(id: $id) {
      ...ShareDocletFields
    }
  }
  ${shareDocletFields}
`
